import { useCallback, useMemo } from 'react';

import { Form, Input } from '@arco-design/web-react';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { applyContainerImageItemToForm } from '@/components-biz/ImageSelect';

import { ImagePullAuthFields } from '../../components/imagePullAuth';
import { ImageResourceSelectField, resolveImageSummaryFromContainerForm } from '../../components/imageResourceSelect';
import type { CapabilityControlProps } from '../../core/types';
import { isFieldDisabled, isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isActionVisible, isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { shouldSkipCapabilityFetch } from '../../platform/capability-registry/shouldSkipCapabilityFetch';
import { clearStoredContainerDeployConfig } from '../../platform/container-deploy-config';
import { buildOptionFromRaw, syncOptionMetaFromRaw } from '../../platform/option-meta';
import { IMAGE_ACTION_KEYS, IMAGE_FIELD_KEYS, IMAGE_OPTION_META_KEYS } from './fieldKeys';
import styles from './index.module.less';
import { coerceImagePaths } from './resolvePaths';
import type { ImageBlockProps } from './types';
import { buildImageMappings, collectHiddenMappingFields } from './utils';

const FormItem = Form.Item;

function isImageInteractionLocked(control: CapabilityControlProps, imageReadonly: boolean): boolean {
  if (imageReadonly) return true;
  return !isActionVisible(IMAGE_ACTION_KEYS.reselect, control);
}

/** 通用镜像选择能力块：只负责镜像选择与元数据映射，系统盘见 system-disk-size */
export function ImageBlock(props: ImageBlockProps) {
  const { form, paths: rawPaths, options, linkageProps, ...control } = props;
  const paths = useMemo(() => coerceImagePaths(rawPaths, options), [options, rawPaths]);

  const hideWhenField = options?.hideWhen?.field;
  const hideWhenValue = options?.hideWhen?.value;
  const hideWhenWatched = Form.useWatch(hideWhenField || '__unused_hide_when__', form);

  const skipImageFetch = shouldSkipCapabilityFetch(control, {
    skipFetch: options?.skipFetch,
    fieldKeys: [IMAGE_FIELD_KEYS.image],
  });

  const watchedImageKey = Form.useWatch(paths.imageKey, form);
  const watchedImageUrl = Form.useWatch(paths.imageUrl, form);

  const imageMappings = useMemo(
    () =>
      buildImageMappings(paths.imagePrefix, {
        legacyFlatFields: options?.legacyFlatFields,
        extraImageMappings: options?.extraImageMappings,
        syncArchitectureFromImage: options?.syncArchitectureFromImage,
      }),
    [options?.extraImageMappings, options?.legacyFlatFields, options?.syncArchitectureFromImage, paths.imagePrefix],
  );
  const hiddenFields = useMemo(() => collectHiddenMappingFields(imageMappings), [imageMappings]);

  const queryObj = useMemo(() => linkageProps?.imageQuery ?? options?.imageQuery ?? {}, [linkageProps?.imageQuery, options?.imageQuery]);

  /** 容器交付默认 container + system_image；裸金属等场景通过 options 设为 common */
  const imageListVariant = options?.imageListVariant ?? 'container';
  const isContainerImageList = imageListVariant === 'container';
  const imageArtifactType = isContainerImageList ? (options?.artifactType ?? 'system_image') : undefined;
  const imageLockArtifactType = isContainerImageList ? (options?.lockArtifactType ?? true) : undefined;

  const imageReadonly = isFieldReadonly(IMAGE_FIELD_KEYS.image, control);
  const imageDisabled = isFieldDisabled(IMAGE_FIELD_KEYS.image, control);
  const imageLocked = isImageInteractionLocked(control, imageReadonly || imageDisabled);

  const presetImageLocked = options?.presetImageLocked === true;
  /** 下单：SKU 预置已有镜像时仅锁删改，仍允许空态选择 */
  const lockActionsWhenFilled = options?.lockActionsWhenFilled === true;
  const presetHasImage = options?.presetHasImage === true;

  const effectiveImageLocked = imageLocked || presetImageLocked || skipImageFetch;

  const imageSummarySource = useMemo(() => {
    return resolveImageSummaryFromContainerForm(form.getFieldsValue() as Record<string, unknown>, paths);
  }, [form, paths, watchedImageKey, watchedImageUrl]);

  const handleImageClear = useCallback(() => {
    hiddenFields.forEach((field) => {
      form.setFieldValue(field, undefined);
    });
    clearStoredContainerDeployConfig(form);
  }, [form, hiddenFields]);

  const handleImageChange = useCallback(
    async (_key: string, item: CommonImageItemType, imageUrl?: string) => {
      if (effectiveImageLocked) return;
      applyContainerImageItemToForm(form, item, imageMappings);
      const resolvedUrl = imageUrl ?? String(item.dockerRefUrl ?? '').trim();
      if (resolvedUrl) {
        form.setFieldValue(paths.imageUrl, resolvedUrl);
      }
      const imageKey = String(item.id ?? item.key ?? '').trim();
      if (imageKey) {
        const built = buildOptionFromRaw(imageKey, item as unknown as Record<string, unknown>);
        // 资源 id 与稳定键双写：linkage 按 imageKey 查，摘要/兼容路径读 optionMeta.image
        syncOptionMetaFromRaw(form, imageKey, built);
        syncOptionMetaFromRaw(form, imageKey, built, IMAGE_OPTION_META_KEYS.image);
      }
      options?.onImageItemSelected?.(item);
    },
    [effectiveImageLocked, form, imageMappings, options, paths.imageUrl],
  );

  if (hideWhenField && hideWhenValue != null && hideWhenWatched === hideWhenValue) {
    return null;
  }

  if (!isFieldVisible(IMAGE_FIELD_KEYS.image, control)) {
    return null;
  }

  return (
    <div className={styles.imageSection}>
      <div className={styles.imageGroup}>
        <ImageResourceSelectField
          form={form}
          queryObj={queryObj}
          variant={imageListVariant}
          artifactType={imageArtifactType}
          lockArtifactType={imageLockArtifactType}
          imageKeyField={paths.imageKey}
          imageUrlField={paths.imageUrl}
          imagePrefix={paths.imagePrefix}
          readOnly={effectiveImageLocked}
          lockActionsWhenFilled={lockActionsWhenFilled}
          presetHasImage={presetHasImage}
          summarySource={imageSummarySource}
          onImageChange={handleImageChange}
          onImageClear={handleImageClear}
        />
      </div>
      {hiddenFields.map((field) => (
        <FormItem key={field} field={field} hidden>
          <Input />
        </FormItem>
      ))}
      {options?.showPullAuth ? <ImagePullAuthFields form={form} disabled={effectiveImageLocked} /> : null}
    </div>
  );
}
